// All backend endpoint paths in one place.
// Change a path here without touching feature code.

export const ENDPOINTS = {
  auth: {
    login: "/api/auth/login",
    me: "/api/auth/me",
  },

  users: {
    list: "/api/admin/users",
    create: "/api/admin/users",
    update: (userId) => `/api/admin/users/${userId}`,
    delete: (userId) => `/api/admin/users/${userId}`,
    status: (userId) => `/api/admin/users/${userId}/status`,
  },

  bottleTypes: {
    list: "/api/admin/bottle-types",
    create: "/api/admin/bottle-types",
    update: (id) => `/api/admin/bottle-types/${id}`,
    delete: (id) => `/api/admin/bottle-types/${id}`,
    status: (id) => `/api/admin/bottle-types/${id}/status`,
  },

  bottleCompanies: {
    list: "/api/admin/bottle-companies",
    create: "/api/admin/bottle-companies",
    update: (id) => `/api/admin/bottle-companies/${id}`,
    status: (id) => `/api/admin/bottle-companies/${id}/status`,
  },

  bottleProducts: {
    list: "/api/admin/bottles",
    active: "/api/admin/bottles/active",
    create: "/api/admin/bottles",
    update: (id) => `/api/admin/bottles/${id}`,
    status: (id) => `/api/admin/bottles/${id}/status`,
  },

  biteCategories: {
    list: "/api/admin/bite-categories",
    create: "/api/admin/bite-categories",
    update: (id) => `/api/admin/bite-categories/${id}`,
    delete: (id) => `/api/admin/bite-categories/${id}`,
    status: (id) => `/api/admin/bite-categories/${id}/status`,
  },

  biteProducts: {
    list: "/api/admin/bites",
    activeStockTracked: "/api/admin/bites/active-stock-tracked",
    create: "/api/admin/bites",
    update: (id) => `/api/admin/bites/${id}`,
    status: (id) => `/api/admin/bites/${id}/status`,
    outletSettings: (id) => `/api/admin/bites/${id}/outlet-settings`,
    outletSetting: (id, outletCode) =>
      `/api/admin/bites/${id}/outlet-settings/${encodeURIComponent(outletCode)}`,
  },

  stock: {
    journey: (source, batchId) => `/api/admin/stock-batches/${source}/${batchId}/journey`,
    list: "/api/admin/bottle-stocks",
    adjustableBatches: "/api/admin/bottle-stocks/adjustments/adjustable-batches",
    create: "/api/admin/bottle-stocks",
    bulkCreate: "/api/admin/bottle-stocks/bulk",
    detail: (id) => `/api/admin/bottle-stocks/${id}`,
    update: (id) => `/api/admin/bottle-stocks/${id}`,
    delete: (id) => `/api/admin/bottle-stocks/${id}`,
    adjustments: "/api/admin/bottle-stocks/adjustments",
    adjustmentUpdate: (id) => `/api/admin/bottle-stocks/adjustments/${id}`,
    adjustmentDelete: (id) => `/api/admin/bottle-stocks/adjustments/${id}`,
    lowStock: "/api/admin/bottle-stocks/low-stock",
  },

  restaurantStock: {
    list: "/api/admin/restaurant-stocks",
    create: "/api/admin/restaurant-stocks",
    bulkCreate: "/api/admin/restaurant-stocks/bulk",
    lowStock: "/api/admin/restaurant-stocks/low-stock",
    detail: (batchId) => `/api/admin/restaurant-stocks/${batchId}`,
    update: (batchId) => `/api/admin/restaurant-stocks/${batchId}`,
    delete: (batchId) => `/api/admin/restaurant-stocks/${batchId}`,
  },

  pos: {
    bottles: "/api/pos/bottles",
    bites: "/api/pos/bites",
    createSale: "/api/pos/sales",
    saleDetail: (saleId) => `/api/pos/sales/${saleId}`,
    mySales: "/api/pos/sales/my",
    mySalesPage: "/api/pos/sales/my/page",
    correctionRequest: (saleId) =>
      `/api/pos/sales/${saleId}/correction-request`,
    cashOpen: "/api/pos/cash-session/open",
    cashCurrent: "/api/pos/cash-session/current",
    cashClose: "/api/pos/cash-session/close",
  },

  cashSettlements: {
    current: "/api/pos/cash-settlements/current",
    history: "/api/pos/cash-settlements/history",
    expenses: "/api/pos/cash-settlements/expenses",
    addExpense: "/api/pos/cash-settlements/expenses",
    voidExpense: (expenseId) =>
      `/api/pos/cash-settlements/expenses/${expenseId}/void`,
    request: "/api/pos/cash-settlements/request",

    adminList: "/api/admin/cash-settlements",
    confirm: (settlementId) =>
      `/api/admin/cash-settlements/${settlementId}/confirm`,
    reject: (settlementId) =>
      `/api/admin/cash-settlements/${settlementId}/reject`,
    reverse: (settlementId) =>
      `/api/admin/cash-settlements/${settlementId}/reverse`,
  },

 restaurantCashSettlements: {
  list: "/api/restaurant/cash-settlements",
  confirm: (settlementId) =>
    `/api/restaurant/cash-settlements/${settlementId}/confirm`,
  reject: (settlementId) =>
    `/api/restaurant/cash-settlements/${settlementId}/reject`,
},

restaurantCashManagement: {
  current: "/api/restaurant/cash-management/current",
  expenses: "/api/restaurant/expenses",
  addExpense: "/api/restaurant/expenses",
  voidExpense: (expenseId) => `/api/restaurant/expenses/${expenseId}/void`,
  ownerSettlements: "/api/restaurant/owner-settlements",
  requestOwnerSettlement: "/api/restaurant/owner-settlement/request",
},

  restaurantOrders: {
    list: "/api/restaurant/orders",
    detail: (id) => `/api/restaurant/orders/${id}`,
    history: (id) => `/api/restaurant/orders/${id}/history`,
  },

  restaurantPrintJobs: {
    claimNext: "/api/restaurant/print-jobs/claim-next",
    printed: (jobId) => `/api/restaurant/print-jobs/${jobId}/printed`,
    failed: (jobId) => `/api/restaurant/print-jobs/${jobId}/failed`,
    retry: (jobId) => `/api/restaurant/print-jobs/${jobId}/retry`,
    reprint: (documentType, documentId) =>
      `/api/restaurant/print-jobs/reprint/${documentType}/${documentId}`,
  },

  adminSales: {
    list: "/api/pos/sales/admin/all",
    manualCorrection: (saleId) =>
      `/api/pos/sales/admin/${saleId}/manual-correction`,
  },

  corrections: {
    list: "/api/admin/correction-requests",
    approve: (id) => `/api/admin/correction-requests/${id}/approve`,
    reject: (id) => `/api/admin/correction-requests/${id}/reject`,
  },

  reports: {
    dailySales: "/api/admin/reports/daily-sales",
    paymentSummary: "/api/admin/reports/payment-summary",
    profit: "/api/admin/reports/profit",
    stockSummary: "/api/admin/reports/stock-summary",
    currentStockValue: "/api/admin/reports/current-stock-value",
  },

  audit: {
    list: "/api/admin/audit-logs",
  },

  cashSessions: {
    list: "/api/admin/cash-sessions",
  },

  notifications: {
    recipients: "/api/admin/notification-recipients",
    recipientStatus: (id) => `/api/admin/notification-recipients/${id}/status`,
    recipientUpdate: (id) => `/api/admin/notification-recipients/${id}`,
    logs: "/api/admin/notification-logs",
    whatsappStatus: "/api/admin/whatsapp/status",
    whatsappConnect: "/api/admin/whatsapp/connect",
    whatsappQr: "/api/admin/whatsapp/qr",
    whatsappDisconnect: "/api/admin/whatsapp/disconnect",
    whatsappGroups: "/api/admin/whatsapp/groups",
    whatsappGroupsSync: "/api/admin/whatsapp/groups/sync",
    notificationRules: "/api/admin/notifications/rules",
    notificationRule: (eventType) => `/api/admin/notifications/rules/${encodeURIComponent(eventType)}`,
    ruleDestinations: (ruleId) => `/api/admin/notifications/rules/${ruleId}/destinations`,
    notificationDestination: (destinationId) => `/api/admin/notifications/destinations/${destinationId}`,
    notificationOutbox: "/api/admin/notifications/outbox",
    notificationRetry: (id) => `/api/admin/notifications/outbox/${id}/retry`,
    notificationResend: (id) => `/api/admin/notifications/outbox/${id}/resend`,
  },
};
